<template>
  <v-layout column pa-0 ma-0>
    <v-flex >
      <v-layout class='graph' fill-height ma-0>
        <v-btn :data-testid="$options.name + '_v-btn_' + 'editMode-true'" v-if="!editMode" small dark color="secondary" @click="editMode = !editMode">
          <span class="caption">{{ $t('edit') }}</span>
        </v-btn>
        <v-btn :data-testid="$options.name + '_v-btn_' + 'editMode-false'" v-if="editMode" small dark color="grey" @click="editMode = !editMode">
          <span class="caption">{{ $t('cancel') }}</span>
        </v-btn>
        <v-btn :data-testid="$options.name + '_v-btn_' + 'saveGraph'" v-if="editMode" small dark color="secondary" @click="saveGraph">
          <span class="caption">{{ $t('save') }}</span>
        </v-btn>
        <v-btn :data-testid="$options.name + '_v-btn_' + 'importNetwork'" v-if="editMode" small dark color="secondary" @click="importNetwork">
          <span class="caption">{{ $t('import') }}</span>
        </v-btn>
        <v-btn :data-testid="$options.name + '_v-btn_' + 'importNetwork-etrueresetbutton'" v-if="editMode" small dark color="green" @click="(e) => importNetwork(e, true, 'reset button')">
          <span class="caption">{{ $t('reset') }}</span>
        </v-btn>    
        
        <v-layout pa-0 ma-0 justify-end>
          <v-btn :data-testid="$options.name + '_v-btn_' + 'importNetwork-truefalseshowdevicesbutton'" right small dark color="secondary" @click="() => {onlyConnectedMode = !onlyConnectedMode; importNetwork(true, false, 'show devices button')}">
            <span v-if="!onlyConnectedMode" class="caption">{{ $t('show only connected devices') }}</span>
            <span v-if="onlyConnectedMode" class="caption">{{ $t('show all devices') }}</span>
          </v-btn>
        </v-layout>
      </v-layout>
      
    </v-flex>

    <div id="g_location_container" class="g_location_container" pa-2></div>

  </v-layout>
</template>

<script lang="ts">
import Vue from 'vue'
import svgIcon from '@/nms/components/media/svg/static/svg-icon.vue'
import * as vis from 'vis-network/standalone'
import { parse } from 'postsvg'
import render from 'posthtml-render'

// Типы
interface VisNode {
  id: string
  $id?: string
  name?: string
  label?: string
  place?: string
  cabinet?: string
  group?: string
  color?: {
    border: string
    background: string
  }
  image?: {
    unselected: string
    selected: string
  }
  x?: number
  y?: number
  [key: string]: any
}

interface VisEdge {
  from: string
  to: string
  id?: string
  $id?: string
  name?: string
  color?: {
    color: string
  }
  smooth?: {
    roundness: number
  }
  [key: string]: any
}

interface VisGraphData {
  nodes: VisNode[]
  connectedNodes: string[]
  lines: VisEdge[]
  deviceIds: string[]
  objects: any[]
}

interface VisOptions {
  height?: string
  width?: string
  edges: {
    smooth: {
      type: string
      forceDirection: string
      roundness?: number
    }
  }
  nodes: {
    shape: string
    image: {
      unselected: string
      selected: string
    }
    borderWidth: number
    size: number
    shapeProperties: {
      useBorderWithImage: boolean
    }
  }
  interaction: {
    selectConnectedEdges: boolean
    dragNodes: boolean
  }
  physics: {
    enabled?: boolean
    forceAtlas2Based: {
      gravitationalConstant: number
      centralGravity: number
      springLength: number
      springConstant: number
      avoidOverlap: number
    }
    maxVelocity: number
    solver: string
    timestep: number
    stabilization: {
      iterations: number
    }
  }
}

interface NetworkData {
  nodes: vis.DataSet | null
  edges: vis.DataSet | null
}

interface GraphData {
  channel: any | null
  connections: any[]
  channelEndpoints: any[]
  devices: any[]
  nodes: any[]
  lines: any[]
}

interface LocationData {
  $id?: string
  id?: string
  name?: string
  cls?: string
  links?: any[]
  _cmdb_?: boolean
  [key: string]: any
}

interface VisConnection {
  point_a?: Array<{ device?: { $id?: string; name?: string; [key: string]: any } }>
  point_b?: Array<{ device?: { $id?: string; name?: string; [key: string]: any } }>
  $id?: string
  name?: string
  [key: string]: any
}

interface TreeNode {
  $id?: string
  cls?: string
  name?: string
  children?: TreeNode[]
  [key: string]: any
}

export default Vue.extend({
  name: 'G_LOCATION',

  components: {
    svgIcon
  },

  props: {
    data: {
      type: Object as () => LocationData,
      default: () => ({})
    },
    visConnections: {
      type: Array as () => VisConnection[],
      default: () => []
    },
  },

  data(): {
    testState: Record<string, any>
    timer: number | null
    editMode: boolean
    onlyConnectedMode: boolean
    visGraphData: VisGraphData
    visContainer: HTMLElement | null
    visOptions: VisOptions
    networkData: NetworkData
    network: any | null
    files: any[] | null
    graphData: GraphData
    testChannels: string[]
    channelId: string
    draw: any | null
    graphNodes: Record<string, any>
    devices: any[]
    resources: any[]
    connections: any[]
    links: any | null
    markers: any | null
    nodes: any | null
    nodeOptions: { personArea: number }
    selectedNode: any | null
    selectedLine: any | null
    nodeDragApprove: boolean
  } {
    return {
      testState: {},
      timer: null,
      editMode: false,
      onlyConnectedMode: false,
      visGraphData: {
        nodes: [],
        connectedNodes: [],
        lines: [],
        deviceIds: [],
        objects: [],
      },
      visContainer: null,
      visOptions: {
        height: '100%',
        width: '100%',
        edges: {
          smooth: {
            type: 'continuous',
            forceDirection: 'none',
          }
        },
        nodes: {
          shape: 'image',
          image: {
            unselected: this.filepath('basket'),
            selected: this.filepath('basket'),
          },
          borderWidth: 3,
          size: 16,
          shapeProperties: {
            useBorderWithImage: true,
          },
        },
        interaction: {
          selectConnectedEdges: false,
          dragNodes: false,
        },
        physics: {
          forceAtlas2Based: {
            gravitationalConstant: -26,
            centralGravity: 0.005,
            springLength: 230,
            springConstant: 0.18,
            avoidOverlap: 1
          },
          maxVelocity: 146,
          solver: 'forceAtlas2Based',
          timestep: 0.35,
          stabilization: {
            iterations: 150
          },
        },
      },
      networkData: {
        nodes: null,
        edges: null,
      },
      network: null,
      files: null,
      graphData: {
        channel: null,
        connections: [],
        channelEndpoints: [],
        devices: [],
        nodes: [],
        lines: [],
      },
      testChannels: ['channel$288', 'channel$306'],
      channelId: 'channel$288',
      draw: null,
      graphNodes: {},
      devices: [],
      resources: [],
      connections: [],
      links: null,
      markers: null,
      nodes: null,
      nodeOptions: { personArea: 100 },
      selectedNode: null,
      selectedLine: null,
      nodeDragApprove: false
    }
  },

  created(): void {
    this.setup('created G_LOCATION setup')
  },

  mounted(): void {
    document.addEventListener('keydown', this.closeOnESC)

    if (window.playwright) {
      window.networkInstance = this.network
      window.networkData = this.networkData
    }
  },

  beforeDestroy(): void {
    document.removeEventListener('keydown', this.closeOnESC)
    this.stopTimer()
  },

  computed: {
    getVisResourcesNodes(): VisNode[] {
      return this.visGraphData.nodes
    },
    getVisResourcesEdges(): VisEdge[] {
      return this.visGraphData.lines
    },
    getData(): LocationData {
      return this.data
    },
  },

  watch: {
    data: {
      handler(newVal: LocationData): void {
        if (!newVal) return
        this.setup('watch G_LOCATION DATA HANDLER')
      },
      deep: true
    },
    'visGraphData.nodes': {
      handler(newVal: VisNode[]): void {
        console.log('WATCH', 'G_LOCATION', 'visGraphData.nodes', newVal)
      },
      deep: true
    },
    editMode: {
      handler(newVal: boolean): void {
        if (newVal) {
          this.$set(this.visOptions.interaction, 'dragNodes', true)
        } else {
          this.$set(this.visOptions.interaction, 'dragNodes', false)
        }
        if (this.network) {
          this.network.setOptions(this.visOptions)
        }
      },
      deep: true
    },
  },

  methods: {
    startTimer(): void {
      this.timer = window.setInterval(() => this.testMethod(), 3000)
    },

    stopTimer(): void {
      if (this.timer) {
        clearTimeout(this.timer)
      }
    },

    testMethod(): void {
      console.log('G_LOCATION', 'testMethod')
      this.testState = this.testState === 'alarm' ? 'ok' : 'alarm'

      this.$store.dispatch('addStatus', {
        $id: 'device$104',
        state: this.testState
      })

      console.log('G_LOCATION', 'testMethod', 'testState', this.testState)
    },

    async setup(caller: string): Promise<void> {
      console.log('G_LOCATION', 'setup caller', caller)
      console.log('G_LOCATION', 'visConnections', this.visConnections)
      this.statusUnsubscribe('G_LOCATION')
      await this.loadFiles('setup')
      await this.prepareNodes()
      await this.importNetwork(true, false, 'setup')
    },

    async checkIcon(node_: VisNode, state?: string): Promise<string> {
      const spl = node_.id.split('@')
      let url$: string

      try {
        url$ = require(`@/nms/components/media/svg/static/files/${spl[1]}.svg`)
      } catch (e) {
        console.log(spl, 'file_not_found', e)
        url$ = require(`@/nms/components/media/svg/static/files/file_not_found.svg`)
      }
      return url$
    },

    filepath(name: string): string {
      const url = require(`@/nms/components/media/svg/static/files/${name}.svg`)
      return url
    },

    async prepareNodes(): Promise<void> {
      if (!this.getData.$id) return
      try {
        let locationTree: TreeNode[] | undefined
        let obj$: any

        if (this.getData._cmdb_) {
          if (this.getData.links?.length) {
            locationTree = this.getData.links
          } else {
            locationTree = await this.getData.getLinks()
          }
        } else {
          const query = {
            id: [this.getData.$id],
            data: {
              links: { flatten: false }
            }
          }
          obj$ = await this.$bridge.cmdbQuery(query)
          locationTree = obj$[0].links
        }

        const arr: VisNode[] = []
        const subscribeArr: TreeNode[] = []

        this.visGraphData = {
          nodes: [],
          connectedNodes: [],
          lines: [],
          deviceIds: [],
          objects: [],
        }

        const heapData = (arr$: TreeNode[], data: TreeNode, parent?: string): void => {
          const parent$ = parent || this.getData.name || obj$?.[0]?.name
          arr$.forEach(child => {
            this.visGraphData.objects.push(child)

            const ownParent$ = child.cls === 'location' ? `${parent$} / ${child.name}` : parent$
            const row$: VisNode = {
              name: child.name,
              label: child.name,
              color: {
                border: '#7F7F7F',
                background: 'transparent',
              },
              $id: child.$id,
              id: `${child.$id}@${this.$checkItem(child)}`,
              place: ownParent$,
              group: data.$id,
            }

            if (child.cls === 'device') {
              row$.cabinet = data.cls === 'cabinet' ? data.name : this.$t('out of cabinet')
              arr.push(row$)
              subscribeArr.push(child)
            }

            if (child.cls === 'module' && data.cls === 'location') {
              row$.cabinet = this.$t('out of device')
              arr.push(row$)
              subscribeArr.push(child)
            }

            if (child.children?.length) {
              heapData(child.children, child, ownParent$)
            }
          })
        }

        heapData(locationTree || [], this.getData)
        this.visGraphData.nodes = arr.length > 5 ? arr.slice(0, 5) : arr
        this.statusSubscribe(this.visGraphData.nodes, 'G_LOCATION')

        this.visGraphData.lines = []
        await this.prepareConnections(this.visGraphData.nodes, 'prepareNodes')
      } catch (err) {
        console.error('CATCH', 'G_LOCATION', 'prepareNodes', err)
      }
    },

    async prepareConnections(nodes: VisNode[], caller: string): Promise<void> {
      console.log('G_LOCATION', 'prepareConnections', 'caller', caller)
      let lines = []
      return
      if(this.visConnections.length) {

        this.visConnections.forEach(connection => {

          this.visGraphData.objects.push(connection)

          if(connection.point_a?.[0].device) {
            // console.log('G_LOCATION', 'prepareConnections', 'point_a', connection.point_a[0].device)
            this.visGraphData.deviceIds.push(connection.point_a[0].device.$id)
            this.visGraphData.connectedNodes.push(connection.point_a[0].device.$id)
            this.visGraphData.objects.push(connection.point_a[0].device)

            if(!nodes.find(n => n.$id == connection.point_a[0].device.$id)) {
              // console.log('G_LOCATION', 'prepareConnections', '!point_a')
              let node = {}
              node.name = connection.point_a[0].device.name
              node.label = connection.point_a[0].device.name
              node.$id = connection.point_a[0].device.$id
              node.id = connection.point_a[0].device.$id + '@' + this.$checkItem(device)
              node.place = 'outer device'
              node.group = 'outer device'
              node.color = { background: "#cccccc", border: "#cccccc", highlight: "#cccccc" }
              nodes.push(node)
            }             
          }
          if(connection.point_b?.[0].device) {
            this.visGraphData.deviceIds.push(connection.point_b[0].device.$id)
            this.visGraphData.connectedNodes.push(connection.point_b[0].device.$id)
            this.visGraphData.objects.push(connection.point_b[0].device)

            if(!nodes.find(n => n.$id == connection.point_b[0].device.$id)) {
              let node = {}
              node.name = connection.point_b[0].device.name
              node.label = connection.point_b[0].device.name
              node.$id = connection.point_b[0].device.$id
              node.id = connection.point_a[0].device.$id + '@' + this.$checkItem(device)
              node.place = 'outer device'
              node.group = 'outer device'
              node.color = { background: "#cccccc", border: "#cccccc", highlight: "#cccccc" }
              nodes.push(node)
            }            
          }

          if(connection.point_a?.[0].device && connection.point_b?.[0].device) {
            let edge = {
              from: connection.point_a[0].device.$id,
              to:  connection.point_b[0].device.$id,
              color: { color: this.$getColor(this.$store.getters.getStatus[connection.$id]) },
              id: connection.$id,
              $id: connection.$id,
              name: connection.name,
              smooth: {roundness: 0.2},
              // color: { color: this.$getColor(this.$store.getters.getStatus[connection.$id]), highlight: this.$getColor(this.$store.getters.getStatus[connection.$id]) },
              // color: { color: "rgba(30,30,30,0.2)", highlight: "blue" },
            }
            let duplication = lines.find(line => line.from == edge.from && line.to == edge.to)
            if(duplication) {
              // console.log('DDDDUUUPPLICAE', edge)
              edge.from = connection.point_b[0].device.$id,
              edge.to = connection.point_a[0].device.$id,
              edge.smooth.roundness = duplication.smooth.roundness + 0.1
              lines.unshift(edge)
            }
            else {
              lines.push(edge)
            }
          }
        })

        // console.log('G_LOCATION', 'prepareConnections', 'nodes', nodes)
        // console.log('G_LOCATION', 'prepareConnections', 'deviceIds', this.visGraphData.deviceIds)
        let checkedDeleteNodes = nodes.filter(n => this.visGraphData.deviceIds.includes(n.$id))
        // console.log('G_LOCATION', 'prepareConnections', 'checkedDeleteNodes', checkedDeleteNodes)

        this.$set(this.visGraphData, 'nodes', checkedDeleteNodes)
        this.$set(this.visGraphData, 'lines', lines)

        return
      }
    },

    stopPhysics(): void {
      setTimeout(() => {
        this.$set(this.visOptions.physics, 'enabled', false)
        if (this.network) {
          this.network.setOptions(this.visOptions)
        }
      }, 2000)
    },

    async saveGraph(): Promise<void> {
      if (!this.getData?.$id) return
      this.editMode = false

      try {
        const $obj = await this.$bridge.getObjectById(this.getData.$id)
        if ($obj) {
          const u = await $obj.attacheDocument(`${this.getData.$id}_graph.json`, this.exportNetwork())
          console.log('G_LOCATION', 'saveGraph', 'update', u)
          await this.loadFiles('saveGraph')
        }
      } catch (error) {
        console.error('CATCH', 'G_LOCATION', 'saveGraph', error)
      }
    },

    exportNetwork(): string {
      const nodes = this.getVisResourcesNodes.map(node => {
        if (this.network) {
          const position = this.network.getPosition(node.id)
          return { ...node, ...position }
        }
        return node
      })
      console.log('G_LOCATION', 'exportNetwork', 'nodes', nodes)
      return JSON.stringify(nodes, undefined, 2)
    },

    async loadFiles(caller?: string): Promise<void> {
      if (!this.getData.$id) return
      try {
        this.setLoader('loadFiles', true)
        this.files = await this.$loadFiles(this.getData.$id)
        this.setLoader('loadFiles', false)
      } catch (err) {
        console.error('CATCH', this.$options.name, 'loadFiles', err)
        this.setLoader('loadFiles', false)
      }
    },

    async importNetwork(e: boolean, restore: boolean, caller: string): Promise<void> {
      console.log('G_LOCATION', 'importNetwork', 'caller', caller)
      try {
        let graph: VisNode[] = []
        let locationTree: TreeNode[] | undefined
        let obj$: any

        this.visGraphData.connectedNodes = []
        this.visGraphData.deviceIds = []
        this.visGraphData.objects = []

        if (this.files?.length && !restore) {
          const graphData = this.files.find(doc$ => doc$.name === `${this.getData.$id}_graph.json`)
          if (graphData) {
            graph = JSON.parse(graphData.content)
          }
        }

        const subscribeArr = this.$merge([], graph)

        if (this.data._cmdb_) {
          if (this.data.links?.length) {
            locationTree = this.data.links
          } else {
            locationTree = await this.data.getLinks()
          }
        } else {
          const query = {
            id: [this.data.$id],
            data: {
              links: { flatten: false }
            }
          }
          obj$ = await this.$bridge.cmdbQuery(query)
          locationTree = obj$[0].links
        }

        const heapData = (arr$: TreeNode[], data: TreeNode, parent?: string): void => {
          const parent$ = parent || this.data.name || obj$?.[0]?.name
          arr$.forEach(child => {
            this.visGraphData.deviceIds.push(child.$id || '')
            this.visGraphData.objects.push(child)

            if (!graph.find(n => n.$id === child.$id)) {
              const ownParent$ = child.cls === 'location' ? `${parent$} / ${child.name}` : parent$
              const row$: VisNode = {
                name: child.name,
                label: child.name,
                color: {
                  border: '#7F7F7F',
                  background: 'transparent',
                },
                $id: child.$id,
                id: `${child.$id}@${this.$checkItem(child)}`,
                place: ownParent$,
                group: data.$id,
              }

              if (child.cls === 'device') {
                row$.cabinet = data.cls === 'cabinet' ? data.name : this.$t('out of cabinet')
                graph.push(row$)
                subscribeArr.push(child)
              }

              if (child.cls === 'module' && data.cls === 'location') {
                row$.cabinet = this.$t('out of device')
                graph.push(row$)
                subscribeArr.push(child)
              }

              if (child.children?.length) {
                heapData(child.children, child, ownParent$)
              }
            }
          })
        }

        heapData(locationTree || [], this.data)

        await this.prepareConnections(graph, 'importNetwork')

        if (this.onlyConnectedMode) {
          this.visGraphData.nodes = this.visGraphData.nodes.filter(n => this.visGraphData.connectedNodes.includes(n.$id || ''))
        }

        for (const node of this.getVisResourcesNodes) {
          const spl = node.id.split('@')
          if (spl?.length > 1) {
            node.image = {
              unselected: await this.checkIcon(node),
              selected: await this.checkIcon(node),
            }
          } else {
            node.image = {
              unselected: await this.checkIcon({ id: 'basket' } as VisNode),
              selected: await this.checkIcon({ id: 'basket' } as VisNode),
            }
          }

          if (graph.length) {
            const gNode = graph.find(n => n.$id === node.$id)
            if (gNode) {
              node.x = gNode.x
              node.y = gNode.y
            }
          }
        }

        const nodes = new vis.DataSet()
        nodes.add(this.getVisResourcesNodes)
        this.$set(this.networkData, 'nodes', nodes)

        const edges = new vis.DataSet()
        edges.add(this.getVisResourcesEdges)
        this.$set(this.networkData, 'edges', edges)

        this.visOptions.physics.enabled = false
        this.visContainer = document.getElementById('g_location_container')

        const visContainerWrapper = document.getElementById('g_location_container_wrapper')
        if (visContainerWrapper) {
          this.$set(this.visOptions, 'height', `${visContainerWrapper.clientHeight}px`)
          this.$set(this.visOptions, 'width', `${visContainerWrapper.clientWidth}px`)
        }

        if (this.visContainer) {
          this.network = new vis.Network(this.visContainer, this.networkData, this.visOptions)
        }

        if (this.network) {
          this.network.on('selectNode', (e: any) => {
            this.visNodeClickHandler(e)
          })
          this.network.on('selectEdge', (e: any) => {
            this.visLineClickHandler(e)
          })
        }

        this.editMode = false
      } catch (error) {
        console.log('CATCH', 'G_LOCATION', 'importNetwork', 'smth wrong', error)
      }
    },

    visLineClickHandler(e: any): void {
      if (!this.network) return
      const edge = this.network.getEdgeAt(e.pointer.DOM)
      const obj = this.visGraphData.objects.find((o: any) => o.$id === edge)
    },

    async visNodeClickHandler(e: any): Promise<void> {
      if (!this.network) return
      const _node = this.network.getNodeAt(e.pointer.DOM)
      const node = this.networkData.nodes?.get(_node)
    },

    async statusSubscribe(arr: any[], group: string): Promise<void> {
      this.$store.dispatch('measuresSubscribeInit', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })

      if (arr.length) {
        await this.$bridge.subscribe(arr.map(obj => obj.$id))
      }
    },

    async subscribeHandler(e: { $id?: string; state?: { infoState?: string; value?: string }; [key: string]: any }): Promise<void> {
      this.$store.dispatch('addStatus', e)

      if (e?.$id && e.$id.split('$')[0] === 'device') {
        const f = this.getVisResourcesNodes.find(node => node.$id === e.$id)
        if (f && this.networkData.nodes) {
          const state = e.state?.infoState || e.state?.value || e.state || 'unknown'
          const color = this.$getColor(state)
          this.networkData.nodes.updateOnly({
            id: f.id,
            color: {
              border: color,
              background: 'transparent',
            }
          })
        }
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    closeOnESC(e: KeyboardEvent): void {
      if (e.which === 27 || e.keyCode === 27 || e.ctrlKey) {
        e.stopPropagation()
        this.selectedLine = null
        this.selectedNode = null
        this.nodeDragApprove = true
      }
    },

    setLoader(name: string, status: boolean): void {
      this.$store.dispatch('setLoader', {
        key: name,
        value: status
      })
    },

    reset(): void {
      this.editMode = false
      this.graphData = {
        channel: null,
        connections: [],
        channelEndpoints: [],
        devices: [],
        nodes: [],
        lines: [],
      }
      this.visOptions = {
        edges: {
          smooth: {
            type: 'continuous',
            forceDirection: 'none',
            roundness: 0.5
          }
        },
        nodes: {
          shape: 'image',
          image: {
            unselected: this.filepath('basket'),
            selected: this.filepath('basket'),
          },
          borderWidth: 3,
          size: 16,
          shapeProperties: {
            useBorderWithImage: true,
          },
        },
        interaction: {
          selectConnectedEdges: false,
          dragNodes: false,
        },
        physics: {
          forceAtlas2Based: {
            gravitationalConstant: -26,
            centralGravity: 0.005,
            springLength: 230,
            springConstant: 0.18,
            avoidOverlap: 1
          },
          maxVelocity: 146,
          solver: 'forceAtlas2Based',
          timestep: 0.35,
          stabilization: {
            iterations: 150
          },
        },
      }
      this.visGraphData = {
        nodes: [],
        connectedNodes: [],
        lines: [],
        deviceIds: [],
        objects: [],
      }
      this.visContainer = null
      this.network = null
      this.networkData = { nodes: null, edges: null }
      this.files = null
      this.graphNodes = {}
      this.devices = []
      this.resources = []
      this.connections = []
      this.links = null
      this.markers = null
      this.nodes = null
      this.selectedNode = null
      this.selectedLine = null
    },
  },
})
</script>

<style scoped>
.g_location_container {
  width: 100%;
  height: 100%;
  /* height: 70vh; */
  /* height: ~"calc(100% - 50px)"; */
  /* height: ~"calc(100%)"; */
  /* border: 1px solid black; */
  background-color: #f0f6fa;
  position: relative;
  /* overflow: visible; */
  overflow: hidden;
  box-sizing: border-box;
}

.line {
  width: 4
}

.graph-popup-content {
  position: relative;
  /* min-width: 460px; */
  min-height: 480px;
  /* max-width: 460px; */
  max-height: 480px;
}
.graph-modal-popup {
  position: absolute;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #cccccc;
  bottom: 12px;
  left: -50px;
  min-width: 680px;
  min-height: 480px;
  max-width: 680px;
  max-height: 480px;
  /* left: 500px;
  top: 500px; */
  z-index: 1;
}
.graph-modal-popup:after, .graph-modal-popup:before {
  top: 100%;
  border: solid transparent;
  content: " ";
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}
.graph-modal-popup:after {
  border-top-color: white;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}
.graph-modal-popup:before {
  border-top-color: #cccccc;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}

/* .path {
  stroke-dasharray: 100;
  animation: dash 5s linear;
} */

/* @keyframes dash {
  to {
    stroke-dashoffset: 20;
  }
} */

</style>
